import cn from 'classnames';
import withStyles, {
  createStyles,
  WithStyles,
} from 'raydiant-elements/core/withStyles';
import withThemeSelector from 'raydiant-elements/core/withThemeSelector';
import { Theme } from 'raydiant-elements/theme';
import * as React from 'react';
import { maxPageWidth } from '../constants';
import Media from './Media';

interface MediaBlockProps extends WithStyles<typeof styles> {
  aspectRatio: [number, number];
  media?: React.ReactNode;
  flex?: number;
  reverse?: boolean;
  maxWidth?: number;
  paddingTop?: boolean;
  paddingTopSm?: boolean;
  paddingBottom?: boolean;
  paddingRight?: boolean;
  paddingLeft?: boolean;
  paddingDouble?: boolean;
  backgroundColor?: string;
  backgroundImage?: string;
  children?: React.ReactNode;
}

const MediaBlock: React.FunctionComponent<MediaBlockProps> = ({
  aspectRatio,
  media,
  flex = 1,
  reverse = false,
  maxWidth,
  paddingTop = false,
  paddingTopSm = false,
  paddingBottom = false,
  paddingRight = false,
  paddingLeft = false,
  paddingDouble = false,
  backgroundColor,
  backgroundImage,
  children,
  classes,
}) => (
  <div
    className={cn(
      classes.root,
      paddingTop && classes.paddingTop,
      paddingTopSm && classes.paddingTopSm,
      paddingBottom && classes.paddingBottom,
      paddingDouble && classes.paddingDouble
    )}
    style={{
      backgroundColor,
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
    }}
  >
    <div className={classes.inner} style={{ maxWidth }}>
      <div
        className={cn(classes.contents, reverse && classes.contentsReversed)}
      >
        {children}
      </div>
      <div
        className={cn(
          classes.media,
          paddingRight && classes.paddingRight,
          paddingLeft && classes.paddingLeft,
          reverse && classes.mediaReversed
        )}
        style={{ flex }}
      >
        <Media aspectRatio={aspectRatio}>{media}</Media>
      </div>
    </div>
  </div>
);

const styles = (theme: Theme) =>
  createStyles({
    root: {
      color: theme.palette.text.primary,
      background: theme.palette.background.default,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
    },
    paddingTop: {
      paddingTop: theme.spacing.unit * 10,
    },
    paddingTopSm: {
      paddingTop: theme.spacing.unit * 4,
    },
    paddingBottom: {
      paddingBottom: theme.spacing.unit * 10,
    },
    paddingDouble: {
      '&$paddingTop': {
        paddingTop: theme.spacing.unit * 20,
      },
      '&$paddingTopSm': {
        paddingTop: theme.spacing.unit * 8,
      },
      '&$paddingBottom': {
        paddingBottom: theme.spacing.unit * 20,
      },
    },
    inner: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: maxPageWidth,
      paddingLeft: theme.spacing.unit * 2,
      margin: '0 auto',

      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 0,
      },
    },
    contents: {
      flex: 1,
      marginRight: theme.spacing.unit * 8,
      paddingTop: theme.spacing.unit,
      paddingBottom: theme.spacing.unit,

      [theme.breakpoints.down('sm')]: {
        marginRight: theme.spacing.unit * 4,
      },

      [theme.breakpoints.down('xs')]: {
        order: 1,
        marginRight: 0,
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit * 5,
        paddingBottom: theme.spacing.unit * 15,
      },
    },
    contentsReversed: {
      order: 1,
      marginLeft: theme.spacing.unit * 8,
      marginRight: 0,
      paddingRight: theme.spacing.unit * 2,

      [theme.breakpoints.down('md')]: {},
      [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
      },
    },
    media: {
      flex: 1,
      paddingRight: theme.spacing.unit * 2,

      [theme.breakpoints.down('sm')]: {
        paddingRight: 0,
      },

      [theme.breakpoints.down('xs')]: {
        order: 0,
        maxHeight: 'none',
      },
    },
    mediaReversed: {
      order: 0,
    },
    mediaFullWidth: {
      paddingRight: 0,
    },
    paddingRight: {
      [theme.breakpoints.down('sm')]: {
        paddingRight: theme.spacing.unit * 2,
      },
    },
    paddingLeft: {
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing.unit * 2,
      },
    },
  });

export default withThemeSelector(withStyles(styles)(MediaBlock), 'medium');
